import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, message, Modal, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import dayjs from "dayjs";
import CreateProjectDrawer from './CreateProject';
import Sidebar from '../Components/SideBar';
import { buildApiUrl } from '../utils/api';

function ProjectList() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all');
    const [deleteModal, setDeleteModal] = useState({ visible: false, projectId: null });
    const [lastRefreshed, setLastRefreshed] = useState("");
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [formData, setFormData] = useState({
        projectName: '',
        projectCapacity: '',
        capacityUnit: 'kWp',
        projectType: '',
        projectStartDate: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
    const [selectedProjectType, setSelectedProjectType] = useState(null);
    const now = dayjs();
    const last30DaysStart = now.subtract(30, 'day');


    const last30DaysProjects = projects.filter(project =>
        dayjs(project.updateDate).isAfter(last30DaysStart)
    );


    const currentCount = last30DaysProjects.length;
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const validateForm = () => {
        if (!formData.projectName.trim()) {
            message.error('Project name is required');
            return false;
        }
        if (!formData.projectCapacity) {
            message.error('Project capacity is required');
            return false;
        }
        if (!formData.projectType) {
            message.error('Project type is required');
            return false;
        }
        if (!formData.projectStartDate) {
            message.error('Project start date is required');
            return false;
        }
        if (!formData.country) {
            message.error('Project Country is required');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        if (!validateForm()) return;

    const apiUrl = buildApiUrl('/api/projects');
        const loadingMessage = message.loading('Creating project...', 0);
        setIsSubmitting(true);

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            await response.json();
            loadingMessage();
            message.success('Project created successfully');
            setIsDrawerOpen(false);
            await fetchProjects();
        } catch (error) {
            loadingMessage();
            message.error('Failed to create project. Please try again later.');
            console.error('Error submitting project data:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDrawerOpen = () => setIsDrawerOpen(true);
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    console.log("token", token)

    const role = localStorage.getItem("role");
    const userId = localStorage.getItem("userid");
    const username = localStorage.getItem("userName")


    const fetchProjects = useCallback(async () => {
        if (!token) {
            console.warn("No token found. Skipping project fetch.");
            setError("Authentication required. Please log in again.");
            return;
        }

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            if (payload.exp <= currentTime) {
                console.warn("Token expired. Skipping project fetch.");
                setError("Session expired. Please log in again.");
                return;
            }
        } catch (err) {
            console.error("Invalid token format. Skipping project fetch.");
            setError("Invalid session. Please log in again.");
            return;
        }

        const hideLoading = message.loading("Loading projects...", 0);
        const apiUrl =
            role === "admin"
                ? buildApiUrl('/api/projects?page=1&limit=100')
                : buildApiUrl(`/api/task/${userId}`);

        try {
            const response = await fetch(apiUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: "application/json",
                },
            });

            const result = await response.json();
            console.log(`${role === "admin" ? "Admin" : "User"} API Response:`, result);

            if (response.ok) {
                const data =
                    role === "admin"
                        ? result.data || []
                        : result.data.flatMap((task) => task.project || []);
                setProjects(data);
            } else {
                throw new Error(result.message || "Unknown error");
            }
        } catch (error) {
            console.error("Fetch error:", error);
            setError("Failed to fetch projects. Please try again later.");
            message.error("Failed to load projects.");
        } finally {
            hideLoading();
            setLoading(false);
        }
    }, [role, token, userId]); // Add dependencies here

    useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);





    const filteredProjects = filter === 'all'
        ? projects
        : projects.filter((project) => project.projectType === filter);


    const handleFilterApply = () => {
        if (selectedProjectType) {
            setFilter(selectedProjectType);
        } else {
            setFilter('all');
        }
        setIsFilterModalVisible(false);
        setCurrentPage(1);
    };


    const showFilterModal = () => {
        setIsFilterModalVisible(true);
    };

    const handleFilterCancel = () => {
        setIsFilterModalVisible(false);
        setSelectedProjectType(null);
    };

    const handleAddTaskClick = (projectId, projectType, country) => {
        navigate('/task-details', { state: { projectId, projectType, country } });
    };

    const showDeleteConfirm = (projectId) => {
        setDeleteModal({ visible: true, projectId });
    };

    const handleDeleteCancel = () => {
        setDeleteModal({ visible: false, projectId: null });
    };

    const handleDeleteConfirm = async () => {
        const projectId = deleteModal.projectId;
        const loadingMessage = message.loading('Deleting project...', 0);

        try {
            const apiUrl = buildApiUrl(`/api/projects/${projectId}`);
            const response = await fetch(apiUrl, {
                method: 'DELETE',
                headers: {
                    "Authorization": `Bearer ${token}`,
                    Accept: 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }

            setProjects((prevProjects) =>
                prevProjects.filter((project) => project._id !== projectId)
            );
            loadingMessage();
            message.success('Project deleted successfully!');
        } catch (error) {
            loadingMessage();
            console.error('Error deleting project:', error);
            message.error('Failed to delete project. Please try again.');
        } finally {
            setDeleteModal({ visible: false, projectId: null });
        }
    };

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

    const paginatedProjects = filteredProjects.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );


    const totalProjects = projects.length;

    const totalCapacity = projects.reduce((sum, project) => {
        return sum + Number(project.projectCapacity || 0);
    }, 0);

    const last30DaysCapacity = projects
        .filter(project => dayjs(project.updateDate).isAfter(last30DaysStart))
        .reduce((sum, project) => {
            return sum + Number(project.projectCapacity || 0);
        }, 0);

    const completedProjects = projects.filter(p => p.overallProgress === 100).length;

    const last30DaysCompletedProjects = projects.filter(
        p => p.overallProgress === 100 && dayjs(p.updateDate).isAfter(last30DaysStart)
    ).length;

    const averageProgress =
        totalProjects > 0
            ? (projects.reduce((sum, p) => sum + Number(p.overallProgress || 0), 0) / totalProjects).toFixed(2)
            : "0.00";

    useEffect(() => {
        const now = new Date();
        const formatted = dayjs(now).format("DD MMM YYYY, hh:mm A");
        setLastRefreshed(formatted);
    }, []);

    console.log("country", paginatedProjects)
    return (
        <div className='flex flex-row h-screen'>
            <Sidebar projectNames={projects.map(p => p.projectName)} />
            <div className="flex flex-col flex-grow  bg-[white] p-6 w-full overflow-hidden">

                <div className="w-full flex justify-between px-8 mb-2 items-start">
                    <div>
                        <p className="text-[16px] text-[black] mb-2 font-normal">Welcome back!</p>
                        <h1 className="text-[24px]  text-[black] font-extrabold">{username}</h1>
                    </div>

                    <div className="flex flex-col justify-end h-full">
                        <p className="text-sm text-gray-500">Last refreshed {lastRefreshed}</p>
                    </div>
                </div>


                <div className="w-full border-b border-gray-300 mb-6"></div>

                <div className="grid grid-cols-4 gap-6 mb-6">
                    {[
                        {
                            title: "Total Projects",
                            value: totalProjects,
                            sub: `${currentCount} created in last 30 days`,
                            color: "text-green-500",
                        },
                        {
                            title: "Total Capacity",
                            value: `${totalCapacity} kWp`,
                            sub: `${last30DaysCapacity} kWp added in last 30 days`,
                            color: "text-green-500",
                        },
                        {
                            title: "Completed Projects",
                            value: completedProjects,
                            sub: `${last30DaysCompletedProjects} completed in last 30 days`,
                            color: "text-red-500",
                        },
                        {
                            title: "Average Progress",
                            value: `${averageProgress}%`,
                            sub: "+2.15% from last month",
                            color: "text-green-500",
                        },
                    ].map((item, idx, arr) => (
                        <div
                            key={idx}
                            className={`pr-6 ${idx !== arr.length - 1 ? "border-r border-gray-300" : ""} flex flex-col items-center text-center`}
                        >
                            <p className="text-sm text-[black] mb-1">{item.title}</p>
                            <h2 className="text-2xl font-bold text-[#0D2B52]">{item.value}</h2>
                            <p className="text-sm">
                                <span className={item.color}>{item.sub.split(" ")[0]}</span>{" "}
                                <span className="text-gray-800">{item.sub.split(" ").slice(1).join(" ")}</span>
                            </p>

                        </div>
                    ))}
                </div>


                <div className="w-full border-b border-gray-300 mb-6"></div>


                <div className="w-full px-8 flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold text-gray-800">Project List</h2>
                    <div className="flex gap-2">
                        <Button
                            className="!border !border-gray-300 !text-[#0D2B52] hover:!text-orange-500 hover:!border-orange-500 transition-colors duration-200"
                            icon={<i className="ri-filter-line"></i>}
                            onClick={showFilterModal}
                        >
                            Filter
                        </Button>

                        {filter !== 'all' && (
                            <Button
                                className="!border !border-gray-300 !text-[#0D2B52] hover:!text-orange-500 hover:!border-orange-500 transition-colors duration-200"
                                onClick={() => {
                                    setFilter('all');
                                    setSelectedProjectType(null);
                                    setCurrentPage(1);
                                }}
                            >
                                Clear Filters
                            </Button>
                        )}



                        <Button

                            className="!border !border-gray-300 !text-[#0D2B52] hover:!text-orange-500 hover:!border-orange-500 transition-colors duration-200"
                            onClick={() => navigate("/daily-log", { state: { projectNames: projects.map(p => p.projectName) } })}
                        >
                            + Add Daily Log
                        </Button>
                        <>
                            <Button
                                className="!text-[white] !bg-[#0D2B52] hover:!text-[#0D2B52] hover:!border-[#0D2B52] hover:!bg-[white] transition-colors duration-200"
                                onClick={handleDrawerOpen}
                            >
                                + Create Project
                            </Button>
                            <CreateProjectDrawer
                                isOpen={isDrawerOpen}
                                onClose={() => setIsDrawerOpen(false)}
                                isSubmitting={isSubmitting}
                                formData={formData}
                                handleInputChange={handleInputChange}
                                handleSelectChange={handleSelectChange}
                                handleSubmit={handleSubmit}
                            />
                        </>
                    </div>
                </div>

                {/* Table */}
                <div className="w-full px-8 flex-grow overflow-auto">
                    {loading ? (
                        <p className="text-center text-gray-500">Loading...</p>
                    ) : error ? (
                        <p className="text-center text-red-500">{error}</p>
                    ) : (
                        <div className="overflow-x-auto mb-5">
                            <table className="min-w-full bg-white rounded-lg shadow border border-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="text-left p-4 font-semibold text-[#838383]">Name</th>
                                        <th className="text-left p-4 font-semibold text-[#838383]">Capacity</th>
                                        <th className="text-left p-4 font-semibold text-[#838383]">Project type</th>
                                        <th className="text-left p-4 font-semibold text-[#838383]">Country</th>
                                        <th className="text-left p-4 font-semibold text-[#838383]">Progress</th>
                                        <th className="text-left p-4 font-semibold text-[#838383]">Last Update</th>
                                        <th className="text-left p-4 font-semibold text-[#838383]">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className='font-semibold'>
                                    {paginatedProjects.map((project) => (
                                        <tr
                                            key={project._id}
                                            className="border-t hover:bg-gray-50 transition duration-200"
                                            onClick={() => handleAddTaskClick(project._id, project.projectType, project.country)}
                                        >
                                            <td className="p-4">{project.projectName}</td>
                                            <td className="p-4">{project.projectCapacity} kWp</td>
                                            <td className="p-4 capitalize">{project.projectType}</td>
                                            <td className="p-4">{project.country || "N/A"}</td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-2 w-24 bg-gray-200 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-2 bg-[#275C96] rounded-full transition-all duration-300"
                                                            style={{ width: `${project.overallProgress || 0}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className="text-sm text-gray-600">{parseFloat(project.overallProgress).toFixed(0)}%</span>
                                                </div>
                                            </td>
                                            <td className="p-4 text-sm text-gray-700">
                                                {project.updateDate ? dayjs(project.updateDate).format("DD/MM/YYYY") : "N/A"}
                                            </td>
                                            <td
                                                className="p-4 text-red-600 hover:text-red-800 cursor-pointer"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showDeleteConfirm(project._id);
                                                }}
                                            >
                                                <DeleteOutlined />
                                            </td>
                                        </tr>

                                    ))}
                                </tbody>
                            </table>
                            {/* Pagination */}
                            <div className="flex gap-1 text-sm text-gray-600 items-center mt-4 justify-end">
                                <Button
                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="custom-orange-btn px-3 py-1 border rounded disabled:opacity-50"
                                >
                                    Prev
                                </Button>

                                {[...Array(totalPages)].map((_, index) => {
                                    const pageNum = index + 1;
                                    return (
                                        <Button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`custom-orange-btn px-3 py-1 rounded border ${currentPage === pageNum
                                                ? "bg-[#EEEEEE] text-[#0D2B52]"
                                                : "bg-white text-[#0D2B52]"
                                                }`}
                                        >
                                            {pageNum}
                                        </Button>
                                    );
                                })}

                                <Button
                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="custom-orange-btn px-3 py-1 border rounded disabled:opacity-50"
                                >
                                    Next
                                </Button>

                            </div>
                        </div>
                    )}
                </div>

                {/* Modal */}
                <Modal
                    title="Delete Project"
                    open={deleteModal.visible}
                    onOk={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true, type: "primary" }}
                    className="custom-delete-modal" // 👈 add this line
                >
                    <p>Are you sure you want to delete this project? This action cannot be undone.</p>
                </Modal>

                <Modal
                    title="Filter Projects"
                    visible={isFilterModalVisible}
                    onOk={handleFilterApply}
                    onCancel={handleFilterCancel}
                    okText="Apply"
                    cancelText="Cancel"
                    okButtonProps={{
                        style: {
                            backgroundColor: '#0D2B52',
                            borderColor: '#0D2B52',
                            color: 'white',
                        },
                        className: 'hover:!bg-white hover:!text-[#0D2B52] hover:!border-[#0D2B52] transition-colors duration-200'
                    }}
                    className="custom-delete-modal"
                >
                    <p>Select Project Type:</p>
                    <Select
                        className="custom-orange-select w-full"
                        placeholder="Select project type"
                        value={selectedProjectType}
                        onChange={(value) => setSelectedProjectType(value)}
                        allowClear
                        onClear={() => setSelectedProjectType(null)}
                    >
                        <Select.Option value="rooftop">Rooftop</Select.Option>
                        <Select.Option value="groundmount">Groundmount</Select.Option>
                    </Select>
                </Modal>

            </div>
        </div>
    );
}

export default ProjectList;